/**
 * Browser-side encode/decode (and hash) implementations. Register in CODECS
 * and extend CodecId when you add one.
 */

import { createTwoFilesPatch, OMIT_HEADERS } from 'diff'

/** Bound work per run—avoids huge strings freezing the tab. */
export const MAX_CODEC_INPUT_CHARS = 400_000

export type CodecId =
  | 'base64_std'
  | 'hex_utf8'
  | 'uri_component'
  | 'uri_full'
  | 'compress_gzip'
  | 'compress_deflate'
  | 'compress_deflate_raw'
  | 'digest_sha_1'
  | 'digest_sha_256'
  | 'digest_sha_384'
  | 'digest_sha_512'
  | 'unicode_nfc'
  | 'unicode_nfd'
  | 'unicode_nfkc'
  | 'unicode_nfkd'
  | 'escape_json'
  | 'escape_c'
  | 'json_format'
  | 'jwt_decode'
  | 'text_diff'
  | 'color_convert'

export type CodecOpResult = string | Promise<string>

export type ToolMode = 'encode' | 'decode'

export type Codec = {
  readonly id: CodecId
  readonly label: string
  readonly statsLabel: string
  readonly encode: (input: string) => CodecOpResult
  readonly decode: (input: string) => CodecOpResult
  readonly hintEncode: string
  readonly hintDecode: string
  readonly placeholderEncode: string
  readonly placeholderDecode: string
  /** Digests / normalize: decode disabled in UI. */
  readonly encodeOnly?: boolean
  /** Uses CompressionStream (feature-detect before run). */
  readonly requiresWebCompression?: boolean
  readonly usesSecondInput?: boolean
  readonly hintSecond?: string
  readonly placeholderSecond?: string
}

function utf8ToBase64Std(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return btoa(binary)
}

function base64StdToUtf8(b64: string): string {
  const binary = atob(b64.trim())
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}

/** RFC 4648 §5 style: `-`/`_`, padding stripped. */
export function utf8ToBase64Url(text: string): string {
  return utf8ToBase64Std(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function padBase64ForAtob(s: string): string {
  const m = s.length % 4
  if (m === 0) return s
  if (m === 1) throw new Error('Invalid base64 length')
  return s + '='.repeat(4 - m)
}

/** Accepts whitespace-stripped base64url; normalizes then decodes as UTF-8. */
export function base64UrlToUtf8(b64: string): string {
  let s = b64.trim().replace(/\s/g, '')
  s = s.replace(/-/g, '+').replace(/_/g, '/')
  s = padBase64ForAtob(s)
  const binary = atob(s)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}

export const BASE64_CODEC_ID = 'base64_std' as const
export const URI_COMPONENT_CODEC_ID = 'uri_component' as const

export const TEXT_DIFF_CODEC_ID = 'text_diff' as const
export const JWT_DECODE_CODEC_ID = 'jwt_decode' as const

function utf8FromBase64UrlSegment(segment: string): string {
  let b = segment.trim().replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/')
  const m = b.length % 4
  if (m === 1) throw new Error('Invalid base64url segment length')
  if (m > 0) b += '='.repeat(4 - m)
  const bin = atob(b)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}

/** Decodes JWT header/payload to pretty JSON. Does not verify signatures. */
export function decodeJwtForDisplay(token: string): string {
  const parts = token.trim().split('.').filter(Boolean)
  if (parts.length < 2) throw new Error('JWT must have at least header and payload')
  const headerRaw = utf8FromBase64UrlSegment(parts[0])
  const payloadRaw = utf8FromBase64UrlSegment(parts[1])
  const header = JSON.stringify(JSON.parse(headerRaw), null, 2)
  const payload = JSON.stringify(JSON.parse(payloadRaw), null, 2)
  let out = `HEADER
${header}

PAYLOAD
${payload}`
  if (parts[2]) {
    const sig = parts[2]
    out += `

SIGNATURE
(opaque base64url; not verified)
${sig.slice(0, 72)}${sig.length > 72 ? '…' : ''}`
  }
  return out
}

/** Avoids jsdiff emitting `\\ No newline at end of file` for typical pasted text. */
function ensureFinalNewline(s: string): string {
  if (s.length === 0) return s
  return s.endsWith('\n') ? s : `${s}\n`
}

/** Unified diff (line-oriented). Large context so typical files are fully shown. */
export function unifiedTextDiff(left: string, right: string): string {
  const patch = createTwoFilesPatch(
    'left',
    'right',
    ensureFinalNewline(left),
    ensureFinalNewline(right),
    '',
    '',
    {
      context: 500_000,
      headerOptions: OMIT_HEADERS,
    },
  )
  return patch ?? ''
}

/** encodeURIComponent; optional `application/x-www-form-urlencoded` space as `+`. */
export function uriComponentEncode(input: string, formStyleSpaceAsPlus: boolean): string {
  let s = encodeURIComponent(input)
  if (formStyleSpaceAsPlus) s = s.replace(/%20/g, '+')
  return s
}

export function uriComponentDecode(input: string, formStyleSpaceAsPlus: boolean): string {
  let s = input.trim()
  if (formStyleSpaceAsPlus) s = s.replace(/\+/g, ' ')
  return decodeURIComponent(s)
}

function utf8ToHex(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let out = ''
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i].toString(16).padStart(2, '0')
  }
  return out
}

function hexToUtf8(hex: string): string {
  const cleaned = hex.replace(/\s/g, '')
  if (cleaned.length === 0) return ''
  if (cleaned.length % 2 !== 0) throw new Error('Hex length must be even')
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) throw new Error('Invalid hex character')
  const bytes = new Uint8Array(cleaned.length / 2)
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(cleaned.slice(i * 2, i * 2 + 2), 16)
  }
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return btoa(binary)
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64.trim().replace(/\s/g, ''))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function compressionAvailable(): boolean {
  return typeof CompressionStream !== 'undefined' && typeof DecompressionStream !== 'undefined'
}

function compressFormat(id: CodecId): CompressionFormat {
  if (id === 'compress_gzip') return 'gzip'
  if (id === 'compress_deflate') return 'deflate'
  return 'deflate-raw'
}

async function compressToBase64(format: CompressionFormat, text: string): Promise<string> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream(format))
  const buf = await new Response(stream).arrayBuffer()
  return bytesToBase64(new Uint8Array(buf))
}

async function decompressFromBase64(format: CompressionFormat, base64: string): Promise<string> {
  const bytes = base64ToBytes(base64)
  const stream = new Blob([new Uint8Array(bytes)]).stream().pipeThrough(new DecompressionStream(format))
  const buf = await new Response(stream).arrayBuffer()
  return new TextDecoder().decode(buf)
}

function makeCompressCodec(
  id: Extract<
    CodecId,
    'compress_gzip' | 'compress_deflate' | 'compress_deflate_raw'
  >,
  label: string,
  stats: string,
): Codec {
  return {
    id,
    label,
    statsLabel: stats,
    encode: async (input: string) => compressToBase64(compressFormat(id), input),
    decode: async (input: string) => decompressFromBase64(compressFormat(id), input),
    hintEncode: 'UTF-8 text',
    hintDecode: 'Base64 (compressed blob)',
    placeholderEncode: 'Text to compress…',
    placeholderDecode: 'Base64 of compressed bytes…',
    requiresWebCompression: true,
  }
}

function hexFromBuffer(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.length; i += 1) s += bytes[i].toString(16).padStart(2, '0')
  return s
}

function makeDigestCodec(
  id: Extract<CodecId, 'digest_sha_1' | 'digest_sha_256' | 'digest_sha_384' | 'digest_sha_512'>,
  label: string,
  algo: AlgorithmIdentifier,
  stats: string,
): Codec {
  return {
    id,
    label,
    statsLabel: stats,
    encode: async (input: string) => {
      const data = new TextEncoder().encode(input)
      const buf = await crypto.subtle.digest(algo, data)
      return hexFromBuffer(buf)
    },
    decode: () => {
      throw new Error('Digests are one-way')
    },
    hintEncode: 'UTF-8 message',
    hintDecode: '—',
    placeholderEncode: 'Input to hash…',
    placeholderDecode: 'Not used',
    encodeOnly: true,
  }
}

function makeNormalizeCodec(id: CodecId, label: string, form: 'NFC' | 'NFD' | 'NFKC' | 'NFKD'): Codec {
  return {
    id,
    label,
    statsLabel: form,
    encode: (input: string) => input.normalize(form),
    decode: () => {
      throw new Error('Normalization is not reversible as a separate step')
    },
    hintEncode: 'Unicode text',
    hintDecode: '—',
    placeholderEncode: 'Text to normalize…',
    placeholderDecode: 'Not used',
    encodeOnly: true,
  }
}

/** JSON / JS string literal escapes (content without surrounding quotes). */
export function jsonFragmentEncode(s: string): string {
  return JSON.stringify(s).slice(1, -1)
}

export function jsonFragmentDecode(s: string): string {
  let i = 0
  let out = ''
  while (i < s.length) {
    const c = s[i]
    if (c !== '\\') {
      if (c === '"' || c < ' ') throw new Error('Unescaped special character; use JSON escapes')
      out += c
      i += 1
      continue
    }
    i += 1
    if (i >= s.length) throw new Error('Invalid escape')
    const e = s[i]
    i += 1
    switch (e) {
      case '"':
        out += '"'
        break
      case '\\':
        out += '\\'
        break
      case '/':
        out += '/'
        break
      case 'b':
        out += '\b'
        break
      case 'f':
        out += '\f'
        break
      case 'n':
        out += '\n'
        break
      case 'r':
        out += '\r'
        break
      case 't':
        out += '\t'
        break
      case 'u': {
        if (i + 4 > s.length) throw new Error('Invalid \\u escape')
        const hex = s.slice(i, i + 4)
        if (!/^[0-9a-fA-F]{4}$/.test(hex)) throw new Error('Invalid \\u escape')
        out += String.fromCharCode(parseInt(hex, 16))
        i += 4
        break
      }
      default:
        throw new Error(`Invalid escape sequence: \\${e}`)
    }
  }
  return out
}

function cStringEncode(s: string): string {
  let out = ''
  for (const c of s) {
    const code = c.charCodeAt(0)
    if (c === '\\') out += '\\\\'
    else if (c === '"') out += '\\"'
    else if (c === "'") out += "\\'"
    else if (c === '\n') out += '\\n'
    else if (c === '\r') out += '\\r'
    else if (c === '\t') out += '\\t'
    else if (c === '\0') out += '\\0'
    else if (code < 0x20 || code === 0x7f) out += `\\x${code.toString(16).padStart(2, '0')}`
    else out += c
  }
  return out
}

function cStringDecode(s: string): string {
  let i = 0
  let out = ''
  while (i < s.length) {
    if (s[i] !== '\\') {
      out += s[i++]
      continue
    }
    i += 1
    if (i >= s.length) throw new Error('Invalid escape')
    const e = s[i++]
    switch (e) {
      case 'n':
        out += '\n'
        break
      case 't':
        out += '\t'
        break
      case 'r':
        out += '\r'
        break
      case '0':
        out += '\0'
        break
      case '\\':
        out += '\\'
        break
      case '"':
        out += '"'
        break
      case "'":
        out += "'"
        break
      case 'x': {
        if (i + 2 > s.length) throw new Error('Invalid \\x')
        const hex = s.slice(i, i + 2)
        if (!/^[0-9a-fA-F]{2}$/.test(hex)) throw new Error('Invalid \\x')
        out += String.fromCharCode(parseInt(hex, 16))
        i += 2
        break
      }
      default:
        throw new Error(`Invalid C escape: \\${e}`)
    }
  }
  return out
}

const base64Std: Codec = {
  id: 'base64_std',
  label: 'Base64 — standard (UTF-8)',
  statsLabel: 'BASE64_STD',
  encode: utf8ToBase64Std,
  decode: base64StdToUtf8,
  hintEncode: 'UTF-8 text',
  hintDecode: 'Base64',
  placeholderEncode: 'Enter raw text…',
  placeholderDecode: 'Paste base64…',
}

const hexUtf8: Codec = {
  id: 'hex_utf8',
  label: 'Hex ↔ UTF-8 bytes',
  statsLabel: 'HEX_UTF8',
  encode: utf8ToHex,
  decode: hexToUtf8,
  hintEncode: 'UTF-8 text',
  hintDecode: 'Hex (pairs; spaces OK)',
  placeholderEncode: 'Text…',
  placeholderDecode: 'deadbeef…',
}

const uriComponent: Codec = {
  id: 'uri_component',
  label: 'URI — encodeURIComponent',
  statsLabel: 'URI_COMPONENT',
  encode: (input: string) => encodeURIComponent(input),
  decode: (input: string) => decodeURIComponent(input.trim()),
  hintEncode: 'UTF-8 text',
  hintDecode: 'Encoded component',
  placeholderEncode: 'Query value, path segment…',
  placeholderDecode: 'hello%20world…',
}

const uriFull: Codec = {
  id: 'uri_full',
  label: 'URI — encodeURI (full URL)',
  statsLabel: 'URI_FULL',
  encode: (input: string) => encodeURI(input),
  decode: (input: string) => decodeURI(input.trim()),
  hintEncode: 'URL string',
  hintDecode: 'Percent-encoded URL',
  placeholderEncode: 'https://example.com/a b…',
  placeholderDecode: 'https://example.com/a%20b…',
}

const escapeJson: Codec = {
  id: 'escape_json',
  label: 'Escapes — JSON / JS string',
  statsLabel: 'JSON_ESC',
  encode: jsonFragmentEncode,
  decode: jsonFragmentDecode,
  hintEncode: 'Raw string',
  hintDecode: 'Escaped fragment (no outer quotes)',
  placeholderEncode: 'Line one\nLine two…',
  placeholderDecode: 'Line one\\nLine two…',
}

const escapeC: Codec = {
  id: 'escape_c',
  label: 'Escapes — C string',
  statsLabel: 'C_ESC',
  encode: cStringEncode,
  decode: cStringDecode,
  hintEncode: 'Raw string',
  hintDecode: 'C escapes (no surrounding quotes)',
  placeholderEncode: 'Hello "world"…',
  placeholderDecode: 'Hello \\"world\\"…',
}

const jsonFormat: Codec = {
  id: 'json_format',
  label: 'JSON — prettify / minify',
  statsLabel: 'JSON',
  encode: (s: string) => JSON.stringify(JSON.parse(s), null, 2),
  decode: (s: string) => JSON.stringify(JSON.parse(s)),
  hintEncode: 'Prettify (2-space indent)',
  hintDecode: 'Minify (one line)',
  placeholderEncode: '{"a":1}…',
  placeholderDecode: 'Same — switch to DEC…',
}

const jwtDecode: Codec = {
  id: 'jwt_decode',
  label: 'JWT — decode (no verify)',
  statsLabel: 'JWT',
  encode: (s: string) => decodeJwtForDisplay(s),
  decode: () => {
    throw new Error('Use ENC to decode JWT')
  },
  hintEncode: 'Paste JWT',
  hintDecode: '—',
  placeholderEncode: 'eyJhbGciOi…',
  placeholderDecode: 'Not used',
  encodeOnly: true,
}

type RgbaColor = { r: number; g: number; b: number; a: number }

function roundTo(n: number, digits: number): number {
  const f = 10 ** digits
  return Math.round(n * f) / f
}

/**
 * Parses any valid CSS color: hex, rgb()/rgba(), hsl()/hsla(), hwb(), oklch(), or a named color such
 * as "cornflowerblue". Uses the browser's own CSS color parser (Canvas 2D `fillStyle`) instead of
 * reimplementing every syntax variant by hand. Per spec, the getter always serializes back as
 * `#rrggbb` (alpha 1) or `rgba(r, g, b, a)`. The regexes below expect one of those two forms.
 */
function parseAnyCssColor(input: string): RgbaColor {
  const trimmed = input.trim()
  if (!trimmed) throw new Error('Enter a CSS color')
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  // fillStyle silently no-ops on an invalid value instead of throwing. A single sentinel check is not
  // enough: a valid input can normalize to that exact sentinel color (for example, "rgb(1, 2, 3)"
  // normalizes to "#010203") and would be misread as invalid. Two distinct seeds fix this. A valid
  // input normalizes to the same result no matter which seed ran first. The two attempts disagree
  // only when neither assignment took effect.
  ctx.fillStyle = '#010203'
  ctx.fillStyle = trimmed
  const attempt1 = ctx.fillStyle
  ctx.fillStyle = '#fefdfc'
  ctx.fillStyle = trimmed
  const attempt2 = ctx.fillStyle
  if (attempt1 !== attempt2) {
    throw new Error('Not a recognized CSS color')
  }
  const normalized = attempt1
  const hexMatch = /^#([0-9a-f]{6})$/i.exec(normalized)
  if (hexMatch) {
    const n = parseInt(hexMatch[1], 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 }
  }
  const rgbaMatch = /^rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\s*\)$/i.exec(normalized)
  if (rgbaMatch) {
    return {
      r: Number(rgbaMatch[1]),
      g: Number(rgbaMatch[2]),
      b: Number(rgbaMatch[3]),
      a: rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1,
    }
  }
  throw new Error('Could not parse normalized color')
}

function toHexFormat({ r, g, b, a }: RgbaColor): string {
  const hex = (n: number) => n.toString(16).padStart(2, '0')
  const base = `#${hex(r)}${hex(g)}${hex(b)}`
  return a >= 1 ? base : `${base}${hex(Math.round(a * 255))}`
}

function toRgbFormat({ r, g, b, a }: RgbaColor): string {
  return a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${roundTo(a, 3)})`
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
  else if (max === gn) h = (bn - rn) / d + 2
  else h = (rn - gn) / d + 4
  return [h * 60, s, l]
}

function toHslFormat(c: RgbaColor): string {
  const [h, s, l] = rgbToHsl(c.r, c.g, c.b)
  const hh = roundTo(h, 1)
  const ss = roundTo(s * 100, 1)
  const ll = roundTo(l * 100, 1)
  return c.a >= 1 ? `hsl(${hh}, ${ss}%, ${ll}%)` : `hsla(${hh}, ${ss}%, ${ll}%, ${roundTo(c.a, 3)})`
}

function srgbToLinear(c: number): number {
  const cn = c / 255
  return cn <= 0.04045 ? cn / 12.92 : Math.pow((cn + 0.055) / 1.055, 2.4)
}

/** sRGB -> OKLCH via Björn Ottosson's OKLab (https://bottosson.github.io/posts/oklab/). */
function rgbToOklch(r: number, g: number, b: number): [number, number, number] {
  const lr = srgbToLinear(r)
  const lg = srgbToLinear(g)
  const lb = srgbToLinear(b)

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb

  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)

  const L = 0.210454255 * l_ + 0.7936177849 * m_ - 0.0040720468 * s_
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
  const b2 = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_

  const C = Math.sqrt(a * a + b2 * b2)
  let H = (Math.atan2(b2, a) * 180) / Math.PI
  if (H < 0) H += 360
  return [L, C, H]
}

function toOklchFormat(c: RgbaColor): string {
  const [L, C, H] = rgbToOklch(c.r, c.g, c.b)
  const Lp = roundTo(L * 100, 1)
  const Cp = roundTo(C, 3)
  const Hp = roundTo(H, 1)
  return c.a >= 1 ? `oklch(${Lp}% ${Cp} ${Hp})` : `oklch(${Lp}% ${Cp} ${Hp} / ${roundTo(c.a, 3)})`
}

/** Parses any CSS color and prints it back as hex, rgb, hsl, and oklch. */
export function convertColorForDisplay(input: string): string {
  const c = parseAnyCssColor(input)
  return `HEX
${toHexFormat(c)}

RGB
${toRgbFormat(c)}

HSL
${toHslFormat(c)}

OKLCH
${toOklchFormat(c)}`
}

/** Parsed rgba() for the swatch preview — kept separate from the formatted text output. */
export function cssRgbaFor(input: string): string {
  const { r, g, b, a } = parseAnyCssColor(input)
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

/** Opaque #rrggbb for <input type="color">, which requires exactly 6 hex digits and has no alpha. */
export function hexForColorPicker(input: string): string {
  const { r, g, b } = parseAnyCssColor(input)
  const hex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${hex(r)}${hex(g)}${hex(b)}`
}

const textDiff: Codec = {
  id: 'text_diff',
  label: 'Diff — unified (lines)',
  statsLabel: 'DIFF',
  encode: (s) => s,
  decode: (s) => s,
  hintEncode: 'Left / “before”',
  hintSecond: 'Right / “after”',
  hintDecode: '—',
  placeholderEncode: 'Original text…',
  placeholderDecode: 'Not used',
  placeholderSecond: 'Changed text…',
  encodeOnly: true,
  usesSecondInput: true,
}

const colorConvert: Codec = {
  id: 'color_convert',
  label: 'Color — convert (hex / rgb / hsl / oklch)',
  statsLabel: 'COLOR',
  encode: (s: string) => convertColorForDisplay(s),
  decode: () => {
    throw new Error('Use ENC to convert')
  },
  hintEncode: 'Any CSS color (hex, rgb, hsl, named, …)',
  hintDecode: '—',
  placeholderEncode: '#3b82f6, rgb(59 130 246), cornflowerblue…',
  placeholderDecode: 'Not used',
  encodeOnly: true,
}

export const COLOR_TOOL_CODECS: Codec[] = [colorConvert]

/** Reversible transport encodings, compression I/O, string escapes. */
export const ENCODING_CODECS: Codec[] = [
  base64Std,
  hexUtf8,
  uriComponent,
  uriFull,
  makeCompressCodec('compress_gzip', 'Compress — gzip (I/O: UTF-8 ↔ base64)', 'GZIP'),
  makeCompressCodec(
    'compress_deflate',
    'Compress — deflate zlib wrapper (UTF-8 ↔ base64)',
    'DEFLATE',
  ),
  makeCompressCodec(
    'compress_deflate_raw',
    'Compress — deflate raw (UTF-8 ↔ base64)',
    'DEFLATE_RAW',
  ),
  escapeJson,
  escapeC,
]

/** Cryptographic digests (one-way). */
export const HASH_CODECS: Codec[] = [
  makeDigestCodec('digest_sha_1', 'Digest — SHA-1', 'SHA-1', 'SHA_1'),
  makeDigestCodec('digest_sha_256', 'Digest — SHA-256', 'SHA-256', 'SHA_256'),
  makeDigestCodec('digest_sha_384', 'Digest — SHA-384', 'SHA-384', 'SHA_384'),
  makeDigestCodec('digest_sha_512', 'Digest — SHA-512', 'SHA-512', 'SHA_512'),
]

/** Unicode normalization forms. */
export const UNICODE_CODECS: Codec[] = [
  makeNormalizeCodec('unicode_nfc', 'Unicode — normalize NFC', 'NFC'),
  makeNormalizeCodec('unicode_nfd', 'Unicode — normalize NFD', 'NFD'),
  makeNormalizeCodec('unicode_nfkc', 'Unicode — normalize NFKC', 'NFKC'),
  makeNormalizeCodec('unicode_nfkd', 'Unicode — normalize NFKD', 'NFKD'),
]

export const JSON_TOOL_CODECS: Codec[] = [jsonFormat]
export const JWT_TOOL_CODECS: Codec[] = [jwtDecode]
export const DIFF_TOOL_CODECS: Codec[] = [textDiff]

/** Full registry (all tools). */
export const CODECS: Codec[] = [
  ...ENCODING_CODECS,
  ...HASH_CODECS,
  ...UNICODE_CODECS,
  ...JSON_TOOL_CODECS,
  ...JWT_TOOL_CODECS,
  ...DIFF_TOOL_CODECS,
  ...COLOR_TOOL_CODECS,
]

export type EncodingCodecId = (typeof ENCODING_CODECS)[number]['id']
export type HashCodecId = (typeof HASH_CODECS)[number]['id']
export type UnicodeCodecId = (typeof UNICODE_CODECS)[number]['id']

export const DEFAULT_CODEC_ID: CodecId = 'base64_std'
export const DEFAULT_ENCODING_CODEC_ID: EncodingCodecId = 'base64_std'
export const DEFAULT_HASH_CODEC_ID: HashCodecId = 'digest_sha_256'
export const DEFAULT_UNICODE_CODEC_ID: UnicodeCodecId = 'unicode_nfc'

export function getCodec(id: string): Codec | undefined {
  return CODECS.find((c) => c.id === id)
}

export function isCodecId(id: string): id is CodecId {
  return CODECS.some((c) => c.id === id)
}

export function isEncodingCodecId(id: string): id is EncodingCodecId {
  return ENCODING_CODECS.some((c) => c.id === id)
}

export function isHashCodecId(id: string): id is HashCodecId {
  return HASH_CODECS.some((c) => c.id === id)
}

export function isUnicodeCodecId(id: string): id is UnicodeCodecId {
  return UNICODE_CODECS.some((c) => c.id === id)
}

export { compressionAvailable }
